import { useState } from 'react';
import axios from 'axios';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { toast } from 'sonner';
import { MapPin, Plus, Trash2, CalendarIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import LocationPicker from './LocationPicker';

export default function CreateClubRideModal({ clubId, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [routeOptions, setRouteOptions] = useState([{ name: '', link: '' }]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [formData, setFormData] = useState({
    time: '',
    distance: '',
    category: '',
    meeting_point: '',
    route_description: '',
    max_riders: ''
  });

  const updateFormField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const addRouteOption = () => {
    if (routeOptions.length < 3) {
      setRouteOptions([...routeOptions, { name: '', link: '' }]);
    }
  };

  const removeRouteOption = (index) => {
    if (routeOptions.length > 1) {
      setRouteOptions(routeOptions.filter((_, i) => i !== index));
    }
  };

  const updateRouteOption = (index, field, value) => {
    const updated = [...routeOptions];
    updated[index][field] = value;
    setRouteOptions(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedLocation) {
      toast.error('Please select a start location');
      return;
    }

    if (!selectedDate) {
      toast.error('Please select a date');
      return;
    }

    const validRoutes = routeOptions.filter(r => r.name && r.link);
    if (validRoutes.length === 0) {
      toast.error('Please add at least one route option');
      return;
    }
    
    setLoading(true);

    const data = {
      date: format(selectedDate, 'yyyy-MM-dd'),
      time: formData.time,
      distance: parseInt(formData.distance),
      category: formData.category,
      latitude: selectedLocation.lat,
      longitude: selectedLocation.lng,
      formatted_address: selectedLocation.address,
      location: formData.meeting_point || '',
      postcode: '',
      route_description: formData.route_description,
      max_riders: formData.max_riders ? parseInt(formData.max_riders) : null,
      ride_type: 'group',
      requires_approval: true,
      club_id: clubId,
      is_club_only: true,
      route_options: validRoutes,
      route_link: validRoutes[0].link
    };

    try {
      await axios.post('/rides', data);
      toast.success('Club ride created successfully!');
      onCreated();
    } catch (error) {
      console.error('[CreateClubRide] Error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to create ride');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!showLocationPicker && (
        <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Club Ride</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Date with Calendar */}
              <div>
                <Label>Date</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal mt-1",
                        !selectedDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {selectedDate ? format(selectedDate, "PPP") : "Pick a date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      disabled={(date) => date < new Date()}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time */}
              <div>
                <Label htmlFor="time">Start Time</Label>
                <Input
                  id="time"
                  type="time"
                  required
                  value={formData.time}
                  onChange={(e) => updateFormField('time', e.target.value)}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="distance">Distance (km)</Label>
                  <Input
                    id="distance"
                    type="number"
                    required
                    min="5"
                    max="500"
                    placeholder="30"
                    value={formData.distance}
                    onChange={(e) => updateFormField('distance', e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="max_riders">Max Riders</Label>
                  <Input
                    id="max_riders"
                    type="number"
                    min="1"
                    max="100"
                    placeholder="No limit"
                    value={formData.max_riders}
                    onChange={(e) => updateFormField('max_riders', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="category">Ride Type</Label>
                <select
                  id="category"
                  required
                  value={formData.category}
                  onChange={(e) => updateFormField('category', e.target.value)}
                  className="w-full p-2 rounded-md bg-gray-800 border border-gray-700 text-white"
                >
                  <option value="">Select ride type...</option>
                  <option value="road">Road</option>
                  <option value="mountain">Mountain</option>
                  <option value="gravel">Gravel</option>
                  <option value="commute">Commute</option>
                </select>
              </div>

              <div>
                <Label>Start Location</Label>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full mt-1 justify-start text-left h-auto py-3"
                  onClick={() => setShowLocationPicker(true)}
                >
                  <MapPin className="w-4 h-4 mr-2 flex-shrink-0 text-purple-500" />
                  {selectedLocation ? (
                    <span className="truncate text-foreground">{selectedLocation.address}</span>
                  ) : (
                    <span className="text-muted-foreground">Tap to select start location</span>
                  )}
                </Button>
              </div>

              <div>
                <Label htmlFor="meeting_point">Meeting Point Details (Optional)</Label>
                <Input
                  id="meeting_point"
                  placeholder="e.g., Car park entrance, near the cafe"
                  value={formData.meeting_point}
                  onChange={(e) => updateFormField('meeting_point', e.target.value)}
                />
              </div>

              <div>
                <Label htmlFor="route_description">Route Description</Label>
                <Textarea
                  id="route_description"
                  required
                  placeholder="Describe the route, terrain, stops..."
                  rows={2}
                  value={formData.route_description}
                  onChange={(e) => updateFormField('route_description', e.target.value)}
                />
              </div>

              {/* Route Options for Voting */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Route Options (for voting)</Label>
                  {routeOptions.length < 3 && (
                    <Button type="button" variant="ghost" size="sm" onClick={addRouteOption}>
                      <Plus className="w-4 h-4 mr-1" /> Add Route
                    </Button>
                  )}
                </div>
                {routeOptions.map((route, index) => (
                  <div key={index} className="p-3 bg-gray-800/50 rounded-lg space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">Route {index + 1}</span>
                      {routeOptions.length > 1 && (
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm"
                          onClick={() => removeRouteOption(index)}
                        >
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </Button>
                      )}
                    </div>
                    <Input
                      placeholder="Route name (e.g., Scenic Loop)"
                      value={route.name}
                      onChange={(e) => updateRouteOption(index, 'name', e.target.value)}
                    />
                    <Input
                      placeholder="Route link (Strava, Garmin, etc.)"
                      value={route.link}
                      onChange={(e) => updateRouteOption(index, 'link', e.target.value)}
                    />
                  </div>
                ))}
                <p className="text-xs text-muted-foreground">
                  Add up to 3 routes for members to vote on
                </p>
              </div>

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" disabled={loading || !selectedLocation || !selectedDate}>
                  {loading ? 'Creating...' : 'Create Club Ride'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      )}

      <LocationPicker
        isOpen={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onLocationSelect={handleLocationSelect}
        initialLocation={selectedLocation}
      />
    </>
  );
}
